import React, { useEffect, useState } from "react";
import { Alert, Badge, Button, Card, CardBody, Container, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner, Table } from "reactstrap";

import { apiFetch, API } from "../../services/api";
import { readError } from "../../helpers/whatsappTemplates";
import { euros, parseEuros, weekLabel } from "../../helpers/displaySpaces";
import { CreativePanel } from "./CreativePanel";
import { GuideLinks } from "./GuideLinks";
import { StatsPanel } from "./StatsPanel";

// Réservations des espaces partenaires : demandes du portail (en attente de devis) et contrats cadres.
const STATUS = {
    pending_quote: ["En attente de devis", "warning"],
    quote_sent: ["Devis envoyé", "info"],
    accepted: ["Acceptée", "success"],
    live: ["En ligne", "primary"],
    done: ["Terminée", "secondary"],
    expired: ["Expirée", "light"],
    cancelled: ["Annulée", "light"],
};
const CANCELLABLE = ["pending_quote", "quote_sent", "accepted"];

export function Bookings() {
    const [list, setList] = useState(null);
    const [spaces, setSpaces] = useState({});
    const [error, setError] = useState(null);
    const [cancel, setCancel] = useState(null);
    const [quote, setQuote] = useState(null); // {b, lines:[{week, listPrice, text}]}
    const [creative, setCreative] = useState(null); // réservation dont on ouvre le contenu
    const [statsOf, setStatsOf] = useState(null); // réservation dont on ouvre les chiffres
    const [spaceObjs, setSpaceObjs] = useState({});
    const [busy, setBusy] = useState(false);

    const load = () => apiFetch(API.back + "/display-bookings-recent", { market: false })
        .then(async (r) => { if (!r.ok) throw new Error(await readError(r)); return r.json(); })
        .then(setList)
        .catch((e) => { setError("Réservations indisponibles : " + e.message); setList([]); });

    useEffect(() => {
        load();
        apiFetch(API.back + "/display-spaces", { market: false }).then((r) => (r.ok ? r.json() : [])).then((d) => {
            const m = {};
            const full = {};
            (Array.isArray(d) ? d : []).forEach((sp) => { m[sp.id] = sp.name?.fr || sp.name?.[sp.market] || sp.id; full[sp.id] = sp; });
            setSpaces(m);
            setSpaceObjs(full);
        }).catch(() => setSpaces({}));
    }, []);

    const doCancel = async () => {
        setBusy(true);
        const res = await apiFetch(API.back + "/display-booking-cancel", { method: "POST", market: false, body: JSON.stringify({ id: cancel.b.id, reason: cancel.reason }) });
        setBusy(false);
        if (!res.ok) { setError(await readError(res)); return; }
        setCancel(null);
        load();
    };

    const openQuote = (b) => {
        setError(null);
        setQuote({ b, lines: (b.lines || []).map((l) => ({ ...l, text: euros(l.price, false) })) });
    };

    const sendQuote = async () => {
        const lines = quote.lines.map((l) => ({ week: l.week, listPrice: l.listPrice, discountPct: l.discountPct, price: parseEuros(l.text) }));
        if (lines.some((l) => l.price === null)) { setError("Prix invalide : saisis un montant en euros (0 accepté)."); return; }
        setBusy(true);
        setError(null);
        const res = await apiFetch(API.back + "/display-booking-quote", { method: "POST", market: false, body: JSON.stringify({ id: quote.b.id, lines }) });
        setBusy(false);
        if (!res.ok) { setError(await readError(res)); return; }
        setQuote(null);
        load();
    };

    const retryInvoice = async (b) => {
        setError(null);
        let checkedInQonto = false;
        if (b.invoiceUncertain || b.invoiceInFlight) {
            // La dernière tentative a peut-être créé la facture : relancer sans vérifier risquerait une 2ᵉ facture.
            if (!window.confirm("La dernière tentative a peut-être créé la facture dans Qonto.\n\nAs-tu vérifié dans Qonto qu'AUCUNE facture n'existe pour ce devis ? (OK = oui, je relance)")) return;
            checkedInQonto = true;
        }
        const res = await apiFetch(API.back + "/display-booking-invoice-retry", { method: "POST", market: false, body: JSON.stringify({ id: b.id, checkedInQonto }) });
        if (!res.ok) { setError(await readError(res)); return; }
        load();
    };

    const quoteTotal = quote ? quote.lines.reduce((sum, l) => sum + (parseEuros(l.text) || 0), 0) : 0;

    return (
        <Container fluid className="p-0">
            <h1 className="h3 mb-1">Réservations des espaces</h1>
            <GuideLinks />
            {error && !cancel && !quote && <Alert color="danger">{error}</Alert>}
            <Card>
                <CardBody>
                    {list === null ? <Spinner /> : list.length === 0 ? <p className="mb-0 text-muted">Aucune réservation.</p> : (
                        <Table hover size="sm" className="mb-0">
                            <thead><tr><th>Créée le</th><th>Partenaire</th><th>Espace</th><th>Semaines</th><th>Total HT</th><th>Statut</th><th /></tr></thead>
                            <tbody>
                                {list.map((b) => {
                                    const [label, color] = STATUS[b.status] || [b.status, "light"];
                                    return (
                                        <tr key={b.id}>
                                            <td>{new Date(b.createdAt).toLocaleDateString("fr-FR")}</td>
                                            <td>{b.partnerName}<div className="small text-muted">{b.createdBy}</div></td>
                                            <td>{spaces[b.spaceId] || b.spaceId}{b.quantity > 1 ? ` × ${b.quantity}` : ""}</td>
                                            <td>{(b.weeks || []).map(weekLabel).join(", ")}{b.report && <div className="small text-muted">Bilan : {b.report.impressions.toLocaleString("fr-FR")} affichages · {b.report.clicks.toLocaleString("fr-FR")} clics · {(b.report.impressions ? (b.report.clicks * 100) / b.report.impressions : 0).toFixed(1).replace(".", ",")} %{b.report.sentAt ? " (envoyé)" : " (envoi en attente)"}</div>}</td>
                                            <td>{b.origin === "framework" ? <span>Contrat cadre{b.contractRef ? ` (${b.contractRef})` : ""}</span>
                                                : b.origin === "direct" ? <span>{euros(b.total)}<div className="small text-muted">Sans devis{b.contractRef ? ` (${b.contractRef})` : ""}</div></span> : euros(b.total)}</td>
                                            <td>
                                                <Badge color={color}>{label}</Badge>
                                                {b.qonto?.paid && <Badge color="success" className="ml-1">payée</Badge>}
                                                {b.invoicePending && <Badge color="danger" className="ml-1">{b.invoiceUncertain ? "facture à vérifier dans Qonto" : "facture en attente"}</Badge>}
                                                {b.qonto?.quoteUrl && <div className="small"><a href={b.qonto.quoteUrl} target="_blank" rel="noopener noreferrer">Devis {b.qonto.quoteNumber}</a></div>}
                                                {b.qonto?.invoiceUrl && <div className="small"><a href={b.qonto.invoiceUrl} target="_blank" rel="noopener noreferrer">Facture {b.qonto.invoiceNumber}</a></div>}
                                                {b.acceptance && <div className="small text-muted">accepté par {b.acceptance.email} le {new Date(b.acceptance.at).toLocaleString("fr-FR")}</div>}
                                            </td>
                                            <td className="text-right">
                                                {b.status === "pending_quote" && <Button size="sm" color="primary" onClick={() => openQuote(b)}>Envoyer le devis</Button>}
                                                {["accepted", "live"].includes(b.status) && spaceObjs[b.spaceId] && <Button size="sm" color={b.creative?.status === "submitted" ? "warning" : "link"} onClick={() => setCreative(b)}>{b.creative?.status === "submitted" ? "Contenu à valider" : "Contenu"}</Button>}
                                                {(["live", "done"].includes(b.status) || b.report) && <Button size="sm" color="link" onClick={() => setStatsOf(b)}>Chiffres</Button>}
                                                {b.invoicePending && <Button size="sm" color="link" onClick={() => retryInvoice(b)}>Réessayer la facture</Button>}
                                                {CANCELLABLE.includes(b.status) && <Button size="sm" color="link" onClick={() => { setError(null); setCancel({ b, reason: "" }); }}>Annuler</Button>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    )}
                </CardBody>
            </Card>

            <Modal isOpen={!!creative} toggle={() => setCreative(null)} size="lg">
                <ModalHeader toggle={() => setCreative(null)}>{creative && `Contenu — ${creative.partnerName} — ${spaces[creative.spaceId] || ""}`}</ModalHeader>
                <ModalBody>{creative && <CreativePanel booking={creative} space={spaceObjs[creative.spaceId]} onChange={() => { setCreative(null); load(); }} />}</ModalBody>
            </Modal>

            <Modal isOpen={!!statsOf} toggle={() => setStatsOf(null)} size="lg">
                <ModalHeader toggle={() => setStatsOf(null)}>{statsOf && `Chiffres — ${statsOf.partnerName} — ${spaces[statsOf.spaceId] || ""}`}</ModalHeader>
                <ModalBody>{statsOf && <StatsPanel booking={statsOf} />}</ModalBody>
            </Modal>

            <Modal isOpen={!!quote} toggle={() => setQuote(null)}>
                <ModalHeader toggle={() => setQuote(null)}>Envoyer le devis</ModalHeader>
                {quote && (
                    <ModalBody>
                        {error && <Alert color="danger">{error}</Alert>}
                        <p>{quote.b.partnerName} — {spaces[quote.b.spaceId] || quote.b.spaceId}. Le devis Qonto (valable 7 jours) part par email aux utilisateurs du partenaire.</p>
                        <Table size="sm">
                            <thead><tr><th>Semaine</th><th>Prix catalogue</th><th>Prix du devis (€ HT)</th></tr></thead>
                            <tbody>
                                {quote.lines.map((l, i) => (
                                    <tr key={l.week}>
                                        <td>{weekLabel(l.week)}</td>
                                        <td>{euros(l.listPrice)}</td>
                                        <td><Input bsSize="sm" value={l.text} onChange={(e) => setQuote({ ...quote, lines: quote.lines.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)) })} /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                        <p className="mb-0"><b>Total HT : {euros(quoteTotal)}</b></p>
                    </ModalBody>
                )}
                <ModalFooter>
                    <Button color="secondary" onClick={() => setQuote(null)}>Fermer</Button>
                    <Button color="primary" disabled={busy} onClick={sendQuote}>{busy ? <Spinner size="sm" /> : "Créer et envoyer le devis Qonto"}</Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={!!cancel} toggle={() => setCancel(null)}>
                <ModalHeader toggle={() => setCancel(null)}>Annuler la réservation</ModalHeader>
                {cancel && (
                    <ModalBody>
                        {error && <Alert color="danger">{error}</Alert>}
                        <p>{cancel.b.partnerName} — {spaces[cancel.b.spaceId] || cancel.b.spaceId} — {(cancel.b.weeks || []).map(weekLabel).join(", ")}. Les semaines redeviennent réservables.</p>
                        <FormGroup><Label>Motif (obligatoire)</Label><Input value={cancel.reason} onChange={(e) => setCancel({ ...cancel, reason: e.target.value })} /></FormGroup>
                    </ModalBody>
                )}
                <ModalFooter>
                    <Button color="secondary" onClick={() => setCancel(null)}>Fermer</Button>
                    <Button color="danger" disabled={busy || !cancel?.reason.trim()} onClick={doCancel}>{busy ? <Spinner size="sm" /> : "Annuler la réservation"}</Button>
                </ModalFooter>
            </Modal>
        </Container>
    );
}
